import {
  assessDesignSystemGenerationRetrieval,
  type DesignSystemGenerationModelCatalog,
  type DesignSystemGenerationRetrievalAdmission,
  type DesignSystemGenerationRetrievalRequest,
} from "./design-system-generation-retrieval";

export const SOURCE_DESIGN_SYSTEM_GENERATION_LINEAGE_SCHEMA_VERSION =
  "cogo.source-design-system-generation-lineage.v1" as const;
export const DESIGN_SYSTEM_GENERATION_AUTHORITY_SCHEMA_VERSION =
  "cogo.design-system-generation-authority.v1" as const;
export const CALLER_BOUNDED_SOURCE_OCCURRENCE_AUTHORITY =
  "caller-bounded-final-studio-gate-required" as const;

export interface SourceDesignSystemGenerationNodeLineage {
  nodeId: string;
  provenanceKind: "source" | "derived";
  sourceFactRefs: string[];
  sourceNodeRefs: string[];
  derivedFromNodeIds: string[];
  designSystemBinding?: {
    requirementId: string;
    componentId: string;
    releaseId: string;
    packageSnapshotHash: string;
    catalogContentHash: string;
    tokenRefs: string[];
    stateRefs: string[];
    variantRefs: string[];
  };
}

export interface SourceDesignSystemGenerationLineageSidecar {
  schemaVersion: typeof SOURCE_DESIGN_SYSTEM_GENERATION_LINEAGE_SCHEMA_VERSION;
  nodes: SourceDesignSystemGenerationNodeLineage[];
  finalJsonWriter: false;
}

export interface SourceDesignSystemGenerationSourceOccurrence {
  readonly sourceNodeRef: string;
  readonly sourceFactRefs: readonly string[];
}

export interface SourceDesignSystemGenerationLocatorIndex {
  readonly sourceOccurrences: readonly SourceDesignSystemGenerationSourceOccurrence[];
}

export interface SourceDesignSystemGenerationLineageReport {
  schemaVersion: "cogo.source-design-system-generation-lineage-report.v1";
  status: "pass" | "fail";
  generationAllowed: boolean;
  blockers: Array<{
    code: string;
    nodeId?: string;
    message: string;
  }>;
  counts: {
    renderableNodeCount: number;
    lineageNodeCount: number;
    sourceLineageNodeCount: number;
    derivedLineageNodeCount: number;
    ontologyBoundNodeCount: number;
    unresolvedNodeCount: number;
  };
  sourceAuthority: {
    sourceId: string;
    fileSha256: string;
    sourceRevision: number;
  };
  designSystemAuthority: {
    releaseId: string | null;
    packageSnapshotHash: string | null;
    catalogContentHash: string | null;
  };
  retrievalAdmission: DesignSystemGenerationRetrievalAdmission;
  finalJsonWriter: false;
}

interface RenderableNode {
  id: string;
  type: string;
  value: Record<string, any>;
}

interface CatalogComponentProperty {
  name: string;
  valueType: string;
  required: boolean;
}

const CANONICAL_EXPRESSION_PATH =
  /^#\{(?:local|event|props|item|_results|computed|appData|env|route)\.[A-Za-z_][A-Za-z0-9_]*(?:\.(?:[A-Za-z_][A-Za-z0-9_]*|\d+))*\}$/;
const CANONICAL_WRITABLE_EXPRESSION_PATH =
  /^#\{(?:local|appData)\.[A-Za-z_][A-Za-z0-9_]*(?:\.(?:[A-Za-z_][A-Za-z0-9_]*|\d+))*\}$/;

export function validateSourceDesignSystemGenerationLineage(input: {
  application: unknown;
  request: DesignSystemGenerationRetrievalRequest;
  retrievalResponse: unknown;
  expectedCatalogContentHash?: string;
  sourceLocatorIndex?: SourceDesignSystemGenerationLocatorIndex;
}): SourceDesignSystemGenerationLineageReport {
  const blockers: SourceDesignSystemGenerationLineageReport["blockers"] = [];
  const retrievalAdmission = assessDesignSystemGenerationRetrieval({
    request: input.request,
    response: input.retrievalResponse,
    ...(input.expectedCatalogContentHash
      ? { expectedCatalogContentHash: input.expectedCatalogContentHash }
      : {}),
  });
  for (const blockerId of retrievalAdmission.blockerIds) {
    blockers.push({
      code: `retrieval:${blockerId}`,
      message: `Design-system retrieval admission failed: ${blockerId}`,
    });
  }

  const application = asRecord(input.application);
  const metadata = asRecord(application?.metadata);
  const sourceAuthority = asRecord(metadata?.sourceAuthority);
  const sourceOccurrenceAuthority = sourceAuthority?.sourceOccurrenceAuthority;
  const designSystemAuthority = asRecord(metadata?.designSystemAuthority);
  const lineage = readLineageSidecar(metadata?.generationLineage);
  const retrieval = asRecord(input.retrievalResponse);
  const catalog = readCatalog(retrieval?.catalog);

  if (!application) {
    blockers.push({
      code: "application-invalid",
      message: "Generated application must be an object.",
    });
  }
  if (!metadata) {
    blockers.push({
      code: "application-metadata-missing",
      message: "Generated application metadata is required.",
    });
  }

  validateSourceAuthority(
    sourceAuthority,
    input.request,
    blockers,
  );
  validateSourceOccurrenceAuthority(
    sourceOccurrenceAuthority,
    input.sourceLocatorIndex !== undefined,
    blockers,
  );
  validateDesignSystemAuthority(
    designSystemAuthority,
    retrievalAdmission,
    blockers,
  );
  if (!lineage) {
    blockers.push({
      code: "generation-lineage-invalid",
      message:
        "A valid source/design-system generation lineage sidecar is required.",
    });
  }

  const renderableNodes = collectRenderableNodes(application);
  if (renderableNodes.length === 0) {
    blockers.push({
      code: "generation-renderable-nodes-missing",
      message: "Generated application must contain renderable UI nodes.",
    });
  }
  const duplicateRenderableNodeIds = duplicateIds(
    renderableNodes.map((node) => node.id),
  );
  for (const nodeId of duplicateRenderableNodeIds) {
    blockers.push({
      code: "generation-renderable-node-duplicate",
      nodeId,
      message: `Generated application contains duplicate node ${nodeId}.`,
    });
  }
  const lineages = lineage?.nodes ?? [];
  const sourceLocatorIndex = buildSourceLocatorIndex(input.sourceLocatorIndex);
  const lineageByNodeId = new Map<
    string,
    SourceDesignSystemGenerationNodeLineage
  >();
  for (const item of lineages) {
    if (sourceLocatorIndex) {
      validateSourceLocatorRefs(item, sourceLocatorIndex, blockers);
    }
    if (lineageByNodeId.has(item.nodeId)) {
      blockers.push({
        code: "generation-lineage-node-duplicate",
        nodeId: item.nodeId,
        message: `Generation lineage contains duplicate node ${item.nodeId}.`,
      });
      continue;
    }
    lineageByNodeId.set(item.nodeId, item);
  }

  const renderableNodeIds = new Set(renderableNodes.map((node) => node.id));
  for (const item of lineages) {
    if (!renderableNodeIds.has(item.nodeId)) {
      blockers.push({
        code: "generation-lineage-node-unknown",
        nodeId: item.nodeId,
        message: `Generation lineage references unknown node ${item.nodeId}.`,
      });
    }
  }

  let sourceLineageNodeCount = 0;
  let derivedLineageNodeCount = 0;
  let ontologyBoundNodeCount = 0;
  let unresolvedNodeCount = 0;
  const boundRequirementIds = new Set<string>();
  for (const node of renderableNodes) {
    const item = lineageByNodeId.get(node.id);
    if (!item) {
      unresolvedNodeCount += 1;
      blockers.push({
        code: "generation-lineage-node-missing",
        nodeId: node.id,
        message: `Renderable node ${node.id} has no generation lineage.`,
      });
      continue;
    }
    if (item.provenanceKind === "source") {
      sourceLineageNodeCount += 1;
      if (
        item.sourceFactRefs.length === 0 ||
        item.sourceNodeRefs.length === 0
      ) {
        unresolvedNodeCount += 1;
        blockers.push({
          code: "generation-source-lineage-incomplete",
          nodeId: node.id,
          message:
            "Source-derived nodes require both sourceFactRefs and sourceNodeRefs.",
        });
      }
      if (item.derivedFromNodeIds.length > 0) {
        unresolvedNodeCount += 1;
        blockers.push({
          code: "generation-source-lineage-shape-mixed",
          nodeId: node.id,
          message:
            "Source nodes cannot carry derivedFromNodeIds; source and derived lineage shapes are mutually exclusive.",
        });
      }
    } else {
      derivedLineageNodeCount += 1;
      if (item.derivedFromNodeIds.length === 0) {
        unresolvedNodeCount += 1;
        blockers.push({
          code: "generation-derived-lineage-incomplete",
          nodeId: node.id,
          message: "Derived nodes require at least one derivedFromNodeId.",
        });
      }
      if (item.sourceFactRefs.length > 0 || item.sourceNodeRefs.length > 0) {
        unresolvedNodeCount += 1;
        blockers.push({
          code: "generation-derived-lineage-shape-mixed",
          nodeId: node.id,
          message:
            "Derived nodes cannot carry sourceFactRefs or sourceNodeRefs; source and derived lineage shapes are mutually exclusive.",
        });
      }
    }

    for (const parentNodeId of item.derivedFromNodeIds) {
      if (parentNodeId === node.id) {
        blockers.push({
          code: "generation-derived-lineage-self-reference",
          nodeId: node.id,
          message: `Derived lineage node ${node.id} cannot reference itself.`,
        });
      }
      if (!renderableNodeIds.has(parentNodeId)) {
        blockers.push({
          code: "generation-derived-lineage-parent-unknown",
          nodeId: node.id,
          message: `Derived lineage parent ${parentNodeId} does not exist.`,
        });
      }
    }

    if (item.designSystemBinding) {
      ontologyBoundNodeCount += 1;
      boundRequirementIds.add(item.designSystemBinding.requirementId);
      validateDesignSystemBinding({
        nodeId: node.id,
        nodeType: node.type,
        node: node.value,
        binding: item.designSystemBinding,
        request: input.request,
        admission: retrievalAdmission,
        catalog,
        blockers,
      });
    } else if (
      catalog?.components.some(
        (component) =>
          component.componentId === node.type &&
          component.targets.includes(input.request.target),
      )
    ) {
      blockers.push({
        code: "generation-lineage-catalog-binding-missing",
        nodeId: node.id,
        message: `Catalog-backed node ${node.id} requires a designSystemBinding.`,
      });
    }
  }
  if (
    !lineages.some(
      (item) =>
        item.provenanceKind === "source" && renderableNodeIds.has(item.nodeId),
    )
  ) {
    blockers.push({
      code: "generation-lineage-source-node-missing",
      message: "Generation lineage requires at least one source node.",
    });
  }
  validateDerivedLineageGraph({
    lineageByNodeId,
    renderableNodeIds,
    sourceLocatorIndex,
    blockers,
  });
  for (const requirementId of retrievalAdmission.matchedRequirementIds) {
    if (!boundRequirementIds.has(requirementId)) {
      blockers.push({
        code: "generation-lineage-required-binding-missing",
        message: `Admitted requirement ${requirementId} is not used by any generated node.`,
      });
    }
  }

  const uniqueBlockers = dedupeBlockers(blockers);
  const generationAllowed =
    retrievalAdmission.generationAllowed && uniqueBlockers.length === 0;
  return {
    schemaVersion: "cogo.source-design-system-generation-lineage-report.v1",
    status: generationAllowed ? "pass" : "fail",
    generationAllowed,
    blockers: uniqueBlockers,
    counts: {
      renderableNodeCount: renderableNodes.length,
      lineageNodeCount: lineages.length,
      sourceLineageNodeCount,
      derivedLineageNodeCount,
      ontologyBoundNodeCount,
      unresolvedNodeCount,
    },
    sourceAuthority: input.request.sourceAuthority,
    designSystemAuthority: {
      releaseId: retrievalAdmission.releaseRef.releaseId,
      packageSnapshotHash: retrievalAdmission.releaseRef.packageSnapshotHash,
      catalogContentHash: retrievalAdmission.releaseRef.catalogContentHash,
    },
    retrievalAdmission,
    finalJsonWriter: false,
  };
}

interface ResolvedSourceDesignSystemGenerationLocatorIndex {
  sourceFactRefs: Set<string>;
  sourceNodeRefs: Set<string>;
  sourceFactRefsByNodeRef: Map<string, Set<string>>;
}

function buildSourceLocatorIndex(
  index: SourceDesignSystemGenerationLocatorIndex | undefined,
): ResolvedSourceDesignSystemGenerationLocatorIndex | null {
  if (!index) return null;
  const sourceFactRefs = new Set<string>();
  const sourceNodeRefs = new Set<string>();
  const sourceFactRefsByNodeRef = new Map<string, Set<string>>();
  for (const occurrence of index.sourceOccurrences) {
    sourceNodeRefs.add(occurrence.sourceNodeRef);
    const occurrenceFactRefs =
      sourceFactRefsByNodeRef.get(occurrence.sourceNodeRef) ?? new Set<string>();
    for (const sourceFactRef of occurrence.sourceFactRefs) {
      sourceFactRefs.add(sourceFactRef);
      occurrenceFactRefs.add(sourceFactRef);
    }
    sourceFactRefsByNodeRef.set(occurrence.sourceNodeRef, occurrenceFactRefs);
  }
  return { sourceFactRefs, sourceNodeRefs, sourceFactRefsByNodeRef };
}

function validateSourceLocatorRefs(
  lineage: SourceDesignSystemGenerationNodeLineage,
  index: ResolvedSourceDesignSystemGenerationLocatorIndex,
  blockers: SourceDesignSystemGenerationLineageReport["blockers"],
): void {
  const unknownSourceFactRefs = lineage.sourceFactRefs.filter(
    (sourceFactRef) => !index.sourceFactRefs.has(sourceFactRef),
  );
  const unknownSourceNodeRefs = lineage.sourceNodeRefs.filter(
    (sourceNodeRef) => !index.sourceNodeRefs.has(sourceNodeRef),
  );
  if (unknownSourceFactRefs.length > 0) {
    blockers.push({
      code: "generation-source-fact-ref-unknown",
      nodeId: lineage.nodeId,
      message:
        `Node ${lineage.nodeId} references source facts absent from the supplied source locator index: ` +
        `${unknownSourceFactRefs.join(", ")}.`,
    });
  }
  if (unknownSourceNodeRefs.length > 0) {
    blockers.push({
      code: "generation-source-node-ref-unknown",
      nodeId: lineage.nodeId,
      message:
        `Node ${lineage.nodeId} references source node locators absent from the supplied source locator index: ` +
        `${unknownSourceNodeRefs.join(", ")}.`,
    });
  }
  if (
    lineage.provenanceKind !== "source" ||
    lineage.sourceFactRefs.length === 0 ||
    lineage.sourceNodeRefs.length === 0 ||
    unknownSourceFactRefs.length > 0 ||
    unknownSourceNodeRefs.length > 0
  ) {
    return;
  }

  const unmatchedSourceFactRefs = lineage.sourceFactRefs.filter(
    (sourceFactRef) =>
      !lineage.sourceNodeRefs.some((sourceNodeRef) =>
        index.sourceFactRefsByNodeRef.get(sourceNodeRef)?.has(sourceFactRef),
      ),
  );
  const unmatchedSourceNodeRefs = lineage.sourceNodeRefs.filter(
    (sourceNodeRef) =>
      !lineage.sourceFactRefs.some((sourceFactRef) =>
        index.sourceFactRefsByNodeRef.get(sourceNodeRef)?.has(sourceFactRef),
      ),
  );
  if (unmatchedSourceFactRefs.length > 0 || unmatchedSourceNodeRefs.length > 0) {
    blockers.push({
      code: "generation-source-fact-node-mismatch",
      nodeId: lineage.nodeId,
      message: `Node ${lineage.nodeId} source fact and node refs do not share the same indexed source occurrence.`,
    });
  }
}

function validateDerivedLineageGraph(input: {
  lineageByNodeId: Map<string, SourceDesignSystemGenerationNodeLineage>;
  renderableNodeIds: Set<string>;
  sourceLocatorIndex: ResolvedSourceDesignSystemGenerationLocatorIndex | null;
  blockers: SourceDesignSystemGenerationLineageReport["blockers"];
}): void {
  const visitState = new Map<string, "visiting" | "visited">();
  const reachesValidSource = new Map<string, boolean>();
  const cycleNodeIds = new Set<string>();

  function visit(nodeId: string, path: string[]): boolean {
    if (!input.renderableNodeIds.has(nodeId)) return false;
    const lineage = input.lineageByNodeId.get(nodeId);
    if (!lineage) return false;
    if (lineage.provenanceKind === "source") {
      return isValidSourceLineage(lineage, input.sourceLocatorIndex);
    }
    if (visitState.get(nodeId) === "visiting") {
      const cycleStart = path.indexOf(nodeId);
      for (const cycleNodeId of path.slice(Math.max(cycleStart, 0))) {
        cycleNodeIds.add(cycleNodeId);
      }
      cycleNodeIds.add(nodeId);
      return false;
    }
    if (visitState.get(nodeId) === "visited") {
      return reachesValidSource.get(nodeId) ?? false;
    }

    visitState.set(nodeId, "visiting");
    path.push(nodeId);
    let allParentsReachValidSource = lineage.derivedFromNodeIds.length > 0;
    for (const parentNodeId of lineage.derivedFromNodeIds) {
      const parentReachesValidSource = visit(parentNodeId, path);
      allParentsReachValidSource =
        allParentsReachValidSource && parentReachesValidSource;
    }
    path.pop();
    visitState.set(nodeId, "visited");
    reachesValidSource.set(nodeId, allParentsReachValidSource);
    return allParentsReachValidSource;
  }

  for (const lineage of input.lineageByNodeId.values()) {
    if (
      lineage.provenanceKind === "derived" &&
      input.renderableNodeIds.has(lineage.nodeId)
    ) {
      visit(lineage.nodeId, []);
    }
  }
  for (const nodeId of [...cycleNodeIds].sort()) {
    input.blockers.push({
      code: "generation-derived-lineage-cycle",
      nodeId,
      message: `Derived lineage for node ${nodeId} contains a cycle.`,
    });
  }
  for (const lineage of input.lineageByNodeId.values()) {
    if (
      lineage.provenanceKind === "derived" &&
      input.renderableNodeIds.has(lineage.nodeId) &&
      reachesValidSource.get(lineage.nodeId) !== true
    ) {
      input.blockers.push({
        code: "generation-derived-lineage-source-unresolved",
        nodeId: lineage.nodeId,
        message: `Every derived lineage chain for node ${lineage.nodeId} must terminate at a valid source node.`,
      });
    }
  }
}

function isValidSourceLineage(
  lineage: SourceDesignSystemGenerationNodeLineage,
  sourceLocatorIndex: ResolvedSourceDesignSystemGenerationLocatorIndex | null,
): boolean {
  if (
    lineage.provenanceKind !== "source" ||
    lineage.sourceFactRefs.length === 0 ||
    lineage.sourceNodeRefs.length === 0 ||
    lineage.derivedFromNodeIds.length > 0
  ) {
    return false;
  }
  if (!sourceLocatorIndex) return true;
  if (
    lineage.sourceFactRefs.some(
      (sourceFactRef) => !sourceLocatorIndex.sourceFactRefs.has(sourceFactRef),
    ) ||
    lineage.sourceNodeRefs.some(
      (sourceNodeRef) => !sourceLocatorIndex.sourceNodeRefs.has(sourceNodeRef),
    )
  ) {
    return false;
  }
  return (
    lineage.sourceFactRefs.every((sourceFactRef) =>
      lineage.sourceNodeRefs.some((sourceNodeRef) =>
        sourceLocatorIndex.sourceFactRefsByNodeRef
          .get(sourceNodeRef)
          ?.has(sourceFactRef),
      ),
    ) &&
    lineage.sourceNodeRefs.every((sourceNodeRef) =>
      lineage.sourceFactRefs.some((sourceFactRef) =>
        sourceLocatorIndex.sourceFactRefsByNodeRef
          .get(sourceNodeRef)
          ?.has(sourceFactRef),
      ),
    )
  );
}

function validateSourceAuthority(
  authority: Record<string, any> | null,
  request: DesignSystemGenerationRetrievalRequest,
  blockers: SourceDesignSystemGenerationLineageReport["blockers"],
): void {
  if (
    !authority ||
    authority.schemaVersion !== "cogo.openpencil-source-authority.v1" ||
    authority.sourceId !== request.sourceAuthority.sourceId ||
    authority.fileSha256 !== request.sourceAuthority.fileSha256 ||
    authority.sourceRevision !== request.sourceAuthority.sourceRevision ||
    authority.finalArtifactWriter !== "deterministic-cogo-compiler" ||
    authority.mayInjectMissingUi !== false ||
    authority.sourceAuthorityViolationCount !== 0
  ) {
    blockers.push({
      code: "application-source-authority-mismatch",
      message:
        "Application sourceAuthority must match the current OpenPencil hash/revision and deterministic compiler boundary.",
    });
  }
}

function validateSourceOccurrenceAuthority(
  authority: unknown,
  hasSourceLocatorIndex: boolean,
  blockers: SourceDesignSystemGenerationLineageReport["blockers"],
): void {
  if (hasSourceLocatorIndex) {
    if (authority !== CALLER_BOUNDED_SOURCE_OCCURRENCE_AUTHORITY) {
      blockers.push({
        code: "application-source-occurrence-authority-mismatch",
        message:
          "A supplied source locator index requires the exact caller-bounded sourceOccurrenceAuthority marker in application metadata.",
      });
    }
    return;
  }
  if (
    authority === CALLER_BOUNDED_SOURCE_OCCURRENCE_AUTHORITY &&
    !hasSourceLocatorIndex
  ) {
    blockers.push({
      code: "generation-source-locator-index-required",
      message:
        "Caller-bounded source occurrences require the active Studio source locator index before generation may be accepted.",
    });
    return;
  }
  if (authority !== undefined) {
    blockers.push({
      code: "application-source-occurrence-authority-mismatch",
      message:
        "Application metadata contains an unsupported sourceOccurrenceAuthority marker.",
    });
  }
}

function validateDesignSystemAuthority(
  authority: Record<string, any> | null,
  admission: DesignSystemGenerationRetrievalAdmission,
  blockers: SourceDesignSystemGenerationLineageReport["blockers"],
): void {
  if (
    !authority ||
    authority.schemaVersion !==
      DESIGN_SYSTEM_GENERATION_AUTHORITY_SCHEMA_VERSION ||
    authority.target !== "web" ||
    authority.packageId !== admission.releaseRef.packageId ||
    authority.packageVersion !== admission.releaseRef.packageVersion ||
    authority.releaseId !== admission.releaseRef.releaseId ||
    authority.packageSnapshotHash !==
      admission.releaseRef.packageSnapshotHash ||
    authority.catalogContentHash !== admission.releaseRef.catalogContentHash ||
    authority.retrievalTraceMode !== "release-catalog" ||
    authority.finalArtifactWriter !== "deterministic-cogo-compiler" ||
    authority.modelFinalJsonWriter !== false
  ) {
    blockers.push({
      code: "application-design-system-authority-mismatch",
      message:
        "Application designSystemAuthority must match the admitted immutable release and deterministic compiler boundary.",
    });
  }
}

function validateDesignSystemBinding(input: {
  nodeId: string;
  nodeType: string;
  node: Record<string, any>;
  binding: NonNullable<
    SourceDesignSystemGenerationNodeLineage["designSystemBinding"]
  >;
  request: DesignSystemGenerationRetrievalRequest;
  admission: DesignSystemGenerationRetrievalAdmission;
  catalog: DesignSystemGenerationModelCatalog | null;
  blockers: SourceDesignSystemGenerationLineageReport["blockers"];
}): void {
  const { binding, blockers, nodeId } = input;
  const requirement = input.request.requirements.find(
    (item) => item.requirementId === binding.requirementId,
  );
  if (!requirement) {
    blockers.push({
      code: "generation-lineage-requirement-unknown",
      nodeId,
      message: `Node ${nodeId} references unknown requirement ${binding.requirementId}.`,
    });
  } else if (
    requirement.componentType &&
    requirement.componentType !== binding.componentId
  ) {
    blockers.push({
      code: "generation-lineage-requirement-component-mismatch",
      nodeId,
      message: `Node ${nodeId} does not use the component admitted for requirement ${binding.requirementId}.`,
    });
  }
  if (
    binding.releaseId !== input.admission.releaseRef.releaseId ||
    binding.packageSnapshotHash !==
      input.admission.releaseRef.packageSnapshotHash ||
    binding.catalogContentHash !== input.admission.releaseRef.catalogContentHash
  ) {
    blockers.push({
      code: "generation-lineage-release-stale",
      nodeId,
      message: `Node ${nodeId} carries a stale design-system release binding.`,
    });
  }
  const component = input.catalog?.components.find(
    (item) => item.componentId === binding.componentId,
  );
  if (
    !component ||
    !component.targets.includes(input.request.target) ||
    component.componentId !== input.nodeType
  ) {
    blockers.push({
      code: "generation-lineage-component-unresolved",
      nodeId,
      message: `Node ${nodeId} component ${binding.componentId} is not an admitted ${input.request.target} catalog component.`,
    });
    return;
  }
  if (
    binding.tokenRefs.some(
      (tokenRef) => !component.admittedContract.tokenRefs.includes(tokenRef),
    ) ||
    binding.stateRefs.some(
      (stateRef) =>
        !component.admittedContract.behavior.states.includes(stateRef),
    ) ||
    binding.variantRefs.some(
      (variantRef) =>
        !component.admittedContract.behavior.variants.includes(variantRef),
    ) ||
    requirement?.stateRefs?.some(
      (stateRef) => !binding.stateRefs.includes(stateRef),
    ) ||
    requirement?.variantRefs?.some(
      (variantRef) => !binding.variantRefs.includes(variantRef),
    )
  ) {
    blockers.push({
      code: "generation-lineage-contract-ref-unresolved",
      nodeId,
      message: `Node ${nodeId} references tokens, states, or variants outside its admitted catalog contract.`,
    });
  }
  validateCatalogBoundRenderableNode({
    nodeId,
    node: input.node,
    component,
    requirement,
    blockers,
  });
}

function validateCatalogBoundRenderableNode(input: {
  nodeId: string;
  node: Record<string, any>;
  component: DesignSystemGenerationModelCatalog["components"][number];
  requirement:
    | DesignSystemGenerationRetrievalRequest["requirements"][number]
    | undefined;
  blockers: SourceDesignSystemGenerationLineageReport["blockers"];
}): void {
  const properties = input.component.properties
    .map(readCatalogComponentProperty)
    .filter(
      (property): property is CatalogComponentProperty => property !== null,
    );
  const invalidRequiredProperties = properties
    .filter((property) => property.required)
    .filter(
      (property) =>
        !isValidCatalogComponentPropertyValue(
          property,
          input.node[property.name],
        ),
    )
    .map((property) => property.name);
  if (invalidRequiredProperties.length > 0) {
    input.blockers.push({
      code: "generation-lineage-node-contract-property-invalid",
      nodeId: input.nodeId,
      message:
        `Catalog-bound node ${input.nodeId} is missing or has invalid required properties: ` +
        `${invalidRequiredProperties.join(", ")}.`,
    });
  }

  if (
    input.component.admittedContract.accessibility.labelRequirement ===
      "required" &&
    !["label", "ariaLabel", "accessibilityLabel"].some((field) =>
      hasText(input.node[field]),
    )
  ) {
    input.blockers.push({
      code: "generation-lineage-node-accessibility-label-missing",
      nodeId: input.nodeId,
      message: `Catalog-bound node ${input.nodeId} requires an explicit accessibility label.`,
    });
  }

  const propertiesByName = new Map(
    properties.map((property) => [property.name, property]),
  );
  const unrepresentedStateRefs = (input.requirement?.stateRefs ?? []).filter(
    (stateRef) => {
      const property = propertiesByName.get(stateRef);
      return (
        property !== undefined &&
        !isValidCatalogComponentPropertyValue(
          property,
          input.node[property.name],
        )
      );
    },
  );
  if (unrepresentedStateRefs.length > 0) {
    input.blockers.push({
      code: "generation-lineage-node-state-unrepresented",
      nodeId: input.nodeId,
      message:
        `Catalog-bound node ${input.nodeId} does not represent requested states in supported node fields: ` +
        `${unrepresentedStateRefs.join(", ")}.`,
    });
  }

  const requestedVariantRefs = (input.requirement?.variantRefs ?? []).filter(
    (variantRef) =>
      input.component.admittedContract.behavior.variants.includes(variantRef),
  );
  if (
    requestedVariantRefs.length > 0 &&
    ![input.node.variant, input.node.variantId].some(
      (value) => hasText(value) && requestedVariantRefs.includes(value),
    )
  ) {
    input.blockers.push({
      code: "generation-lineage-node-variant-unrepresented",
      nodeId: input.nodeId,
      message: `Catalog-bound node ${input.nodeId} does not represent any requested variant in variant or variantId.`,
    });
  }
}

function readCatalogComponentProperty(
  value: unknown,
): CatalogComponentProperty | null {
  const record = asRecord(value);
  if (!record || !hasText(record.name) || !hasText(record.valueType)) {
    return null;
  }
  return {
    name: record.name,
    valueType: record.valueType,
    required: record.required === true,
  };
}

function isValidCatalogComponentPropertyValue(
  property: CatalogComponentProperty,
  value: unknown,
): boolean {
  if (property.name === "options") {
    return (
      Array.isArray(value) &&
      value.length > 0 &&
      value.every((option) => {
        const record = asRecord(option);
        return Boolean(
          record && hasText(record.label) && hasText(record.value),
        );
      })
    );
  }
  switch (property.valueType) {
    case "data-binding":
      return isCanonicalWritableExpression(value);
    case "string":
      return hasText(value);
    case "array":
      return Array.isArray(value);
    case "boolean":
      return typeof value === "boolean" || isCanonicalExpression(value);
    case "number":
      return (
        (typeof value === "number" && Number.isFinite(value)) ||
        isCanonicalExpression(value)
      );
    case "object":
      return asRecord(value) !== null;
    default:
      return value !== undefined && value !== null;
  }
}

function isCanonicalExpression(value: unknown): value is string {
  return typeof value === "string" && CANONICAL_EXPRESSION_PATH.test(value);
}

function isCanonicalWritableExpression(value: unknown): value is string {
  return (
    typeof value === "string" &&
    CANONICAL_WRITABLE_EXPRESSION_PATH.test(value)
  );
}

function collectRenderableNodes(
  application: Record<string, any> | null,
): RenderableNode[] {
  const blocks = asRecord(application?.blocks);
  if (!blocks) return [];
  const nodes: RenderableNode[] = [];
  for (const block of Object.values(blocks)) {
    const blockRecord = asRecord(block);
    const ui = asRecord(blockRecord?.ui);
    collectNode(ui?.root, nodes);
  }
  return nodes;
}

function collectNode(
  value: unknown,
  out: RenderableNode[],
): void {
  const node = asRecord(value);
  if (!node) return;
  if (hasText(node.id) && hasText(node.type)) {
    out.push({ id: node.id, type: node.type, value: node });
  }
  if (Array.isArray(node.children)) {
    for (const child of node.children) collectNode(child, out);
  }
}

function readLineageSidecar(
  value: unknown,
): SourceDesignSystemGenerationLineageSidecar | null {
  const record = asRecord(value);
  if (
    !record ||
    record.schemaVersion !==
      SOURCE_DESIGN_SYSTEM_GENERATION_LINEAGE_SCHEMA_VERSION ||
    record.finalJsonWriter !== false ||
    !Array.isArray(record.nodes)
  ) {
    return null;
  }
  const nodes: SourceDesignSystemGenerationNodeLineage[] = [];
  for (const item of record.nodes) {
    const node = asRecord(item);
    const designSystemBinding =
      node?.designSystemBinding === undefined
        ? undefined
        : readDesignSystemBinding(node.designSystemBinding);
    if (
      !node ||
      !hasText(node.nodeId) ||
      (node.provenanceKind !== "source" && node.provenanceKind !== "derived") ||
      !isStringArray(node.sourceFactRefs) ||
      !isStringArray(node.sourceNodeRefs) ||
      !isStringArray(node.derivedFromNodeIds) ||
      (node.designSystemBinding !== undefined && !designSystemBinding)
    ) {
      return null;
    }
    nodes.push({
      nodeId: node.nodeId,
      provenanceKind: node.provenanceKind,
      sourceFactRefs: [...node.sourceFactRefs],
      sourceNodeRefs: [...node.sourceNodeRefs],
      derivedFromNodeIds: [...node.derivedFromNodeIds],
      ...(designSystemBinding ? { designSystemBinding } : {}),
    });
  }
  return {
    schemaVersion: SOURCE_DESIGN_SYSTEM_GENERATION_LINEAGE_SCHEMA_VERSION,
    nodes,
    finalJsonWriter: false,
  };
}

function readDesignSystemBinding(
  value: unknown,
): NonNullable<
  SourceDesignSystemGenerationNodeLineage["designSystemBinding"]
> | null {
  const record = asRecord(value);
  if (
    !record ||
    !hasText(record.requirementId) ||
    !hasText(record.componentId) ||
    !hasText(record.releaseId) ||
    !hasText(record.packageSnapshotHash) ||
    !hasText(record.catalogContentHash) ||
    !isStringArray(record.tokenRefs) ||
    !isStringArray(record.stateRefs) ||
    !isStringArray(record.variantRefs)
  ) {
    return null;
  }
  return {
    requirementId: record.requirementId,
    componentId: record.componentId,
    releaseId: record.releaseId,
    packageSnapshotHash: record.packageSnapshotHash,
    catalogContentHash: record.catalogContentHash,
    tokenRefs: [...record.tokenRefs],
    stateRefs: [...record.stateRefs],
    variantRefs: [...record.variantRefs],
  };
}

function readCatalog(
  value: unknown,
): DesignSystemGenerationModelCatalog | null {
  const record = asRecord(value);
  return record?.schemaVersion === "cogo.model-component-catalog.v1" &&
    Array.isArray(record.components)
    ? (record as unknown as DesignSystemGenerationModelCatalog)
    : null;
}

function dedupeBlockers(
  blockers: SourceDesignSystemGenerationLineageReport["blockers"],
): SourceDesignSystemGenerationLineageReport["blockers"] {
  const seen = new Set<string>();
  return blockers.filter((blocker) => {
    const key = `${blocker.code}:${blocker.nodeId ?? ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function duplicateIds(values: string[]): string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const value of values) {
    if (seen.has(value)) duplicates.add(value);
    seen.add(value);
  }
  return [...duplicates].sort();
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(hasText);
}

function asRecord(value: unknown): Record<string, any> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, any>)
    : null;
}

function hasText(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}
